import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { predicateBoolean } from 'utilities/isType.js';
import { ConfigWithDataUrl } from './dataUrlHook.js';
import { FetchDataReturn, fetchAsyncData } from './fetchDataAsync.js';

export const REACT_QUERY_STALE_TIME = 5 * 60_000; // 5 minutes
export type CustomError = { isError: boolean; msg: string; url: string };

/**
 * Checks if module has a dataUrl prop
 * If it does then fetch and merge and pass new props to fn
 */
export function UseDataUrlQueryHook({
  dataUrl,
  name,
}: Pick<ConfigWithDataUrl, 'name' | 'dataUrl'>): [
  CustomError,
  FetchDataReturn<ConfigWithDataUrl | null | Record<string, never> | Record<'name', 'remove-module'>> | undefined,
] {
  const [error, setError] = useState<CustomError>({ isError: false, msg: '', url: '' });

  const { data } = useQuery({
    queryKey: ['dataUrl', { url: dataUrl, component: name }] as const,
    queryFn: async ({ signal, queryKey }) => {
      const [, { url }] = queryKey;
      const result = fetchAsyncData<ConfigWithDataUrl>({ url, signal });
      return result;
    },
    enabled: true,
    /**
     * Stale queries are refetched automatically in the background when new instances of the query mount
     * We increase here so when bounding between layouts the data is not fetched again
     */
    staleTime: REACT_QUERY_STALE_TIME,
  });

  useEffect(() => {
    if (data && predicateBoolean(data.responseOK)) {
      if (!data.responseOK) {
        setError({ isError: true, msg: data?.err ?? '', url: name });
      }
    }
  }, [data, name]);

  return [error, data];
}
